import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { FilterForm } from './add-gmail-filter-dialog';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Grow from '@mui/material/Grow';
import Alert from '@mui/material/Alert';

type FilterFormErrors = FieldErrors<FilterForm> & {
  step2Action?: {
    message?: string;
    type: string;
  };
  step1Criteria?: {
    message?: string;
    type: string;
  };
};

interface AddGmailFilterStepOneProps {
  control: Control<FilterForm>;
  setValue: UseFormSetValue<FilterForm>;
  errors: FilterFormErrors;
}

export default function AddGmailFilterStepOne({
  control,
  setValue,
  errors,
}: AddGmailFilterStepOneProps) {
  const sizeComparisonOptions = [
    { value: 'larger', displayValue: 'Greater Than' },
    { value: 'smaller', displayValue: 'Less Than' },
  ];

  const sizeTypeOptions = [
    { value: 'MB', displayValue: 'MB' },
    { value: 'KB', displayValue: 'KB' },
    { value: 'Byte', displayValue: 'Byte' },
  ];

  return (
    <Stack gap={1.5} mt={1} sx={{ px: 2 }}>
      <Controller
        name={'from'}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            slotProps={{
              htmlInput: {
                id: 'from',
              },
              inputLabel: {
                htmlFor: 'from',
              },
            }}
            label="From"
            fullWidth
            autoComplete="off"
          />
        )}
      />
      <Controller
        name={'to'}
        control={control}
        render={({ field }) => (
          <TextField
            slotProps={{
              htmlInput: {
                id: 'to',
              },
              inputLabel: {
                htmlFor: 'to',
              },
            }}
            label="To"
            {...field}
            sx={{ width: '100%' }}
            autoComplete="off"
          />
        )}
      />
      <Controller
        name={'subject'}
        control={control}
        render={({ field }) => (
          <TextField
            slotProps={{
              htmlInput: {
                id: 'subject',
              },
              inputLabel: {
                htmlFor: 'subject',
              },
            }}
            label="Subject"
            {...field}
            sx={{ width: '100%' }}
            autoComplete="off"
          />
        )}
      />
      <Controller
        name={'query'}
        control={control}
        render={({ field }) => (
          <TextField
            slotProps={{
              htmlInput: {
                id: 'query',
              },
              inputLabel: {
                htmlFor: 'query',
              },
            }}
            label="Has the words"
            {...field}
            sx={{ width: '100%' }}
            autoComplete="off"
          />
        )}
      />
      <Controller
        name={'negatedQuery'}
        control={control}
        render={({ field }) => (
          <TextField
            slotProps={{
              htmlInput: {
                id: 'negatedQuery',
              },
              inputLabel: {
                htmlFor: 'negatedQuery',
              },
            }}
            label="Doesn't have"
            {...field}
            sx={{ width: '100%' }}
            autoComplete="off"
          />
        )}
      />
      <Stack flexDirection="row" gap={2} sx={{ width: '100%' }}>
        <Controller
          name={'sizeComparison'}
          control={control}
          render={({ field }) => (
            <TextField
              select
              slotProps={{
                htmlInput: {
                  id: 'sizeComparison',
                },
                inputLabel: {
                  htmlFor: 'sizeComparison',
                },
              }}
              {...field}
              label="Size Comparison"
              sx={{ flexGrow: 1 }}
            >
              {sizeComparisonOptions.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {option.displayValue}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          name={'size'}
          control={control}
          render={({ field }) => (
            <TextField
              slotProps={{
                htmlInput: {
                  id: 'size',
                },
                inputLabel: {
                  htmlFor: 'size',
                },
              }}
              type="number"
              label="Size"
              value={field.value}
              onChange={(event) => {
                if (event.target.value === '') {
                  field.onChange(event.target.value);
                } else if (event.target.value === 'e') {
                  field.onChange('');
                } else {
                  field.onChange(parseInt(event.target.value));
                }
              }}
              sx={{ width: 100 }}
              autoComplete="off"
              error={!!errors.size}
            />
          )}
        />
        <Controller
          name={'sizeType'}
          control={control}
          render={({ field }) => (
            <TextField
              select
              slotProps={{
                htmlInput: {
                  id: 'sizeType',
                },
                inputLabel: {
                  htmlFor: 'sizeType',
                },
              }}
              {...field}
              label="File Size"
              sx={{ width: 80 }}
            >
              {sizeTypeOptions.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                  {option.displayValue}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Stack>
      <Controller
        name={'hasAttachment'}
        control={control}
        render={({ field }) => {
          return (
            <FormControlLabel
              control={
                <Checkbox
                  slotProps={{
                    input: {
                      id: 'hasAttachment',
                    },
                  }}
                  onChange={(e) => {
                    setValue('hasAttachment', e.target.checked, {
                      shouldDirty: true,
                    });
                  }}
                  checked={field.value === true}
                />
              }
              label="Has Attachment"
            />
          );
        }}
      />

      <Stack alignItems={'center'}>
        {errors.step1Criteria ? (
          <Box width={'100%'}>
            <Grow in={true}>
              <Alert severity="error">
                {errors.step1Criteria.message ||
                  'Please select at least one option from this section for your filter criteria.'}
              </Alert>
            </Grow>
          </Box>
        ) : null}
      </Stack>
    </Stack>
  );
}
